/**
 * Realtime transport adapter (WP-3.3 — Realtime → SSE).
 *
 * ONE module that all 5 realtime surfaces subscribe through, with two
 * implementations selected at BUILD time by `REACT_APP_REALTIME_TRANSPORT`:
 *
 *   - 'supabase' (DEFAULT / unset): byte-identical to the pre-WP-3.3 code — each
 *     surface opens the SAME supabase.channel(...) it always did (postgres_changes
 *     with the same filters, or the same broadcast topic/event). R2: nothing about
 *     the Supabase path changes.
 *   - 'sse': a SINGLE EventSource (per site) multiplexes every channel; the server
 *     (GET /api/events) fans out published events. The JWT rides as a query param
 *     (EventSource can't set headers) and is refreshed-and-reconnected on auth
 *     error; exponential backoff on transient drops.
 *
 * The flip is a build-time env change + a static-site redeploy on Render (the
 * frontend is a static bundle — see docs/migration-evidence/wp-3-3-realtime-sse.md).
 *
 * Each surface gets a typed subscribe function so the 5 consumers keep their exact
 * handler contracts; the adapter reshapes an SSE frame back into the same shape the
 * Supabase callback delivered. Every subscribe returns an unsubscribe function.
 */
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { getCurrentSiteId } from '../services/api';

/** Build-time transport selector. Unset/anything-but-'sse' => Supabase (R2 default). */
export const REALTIME_TRANSPORT: 'supabase' | 'sse' =
  process.env.REACT_APP_REALTIME_TRANSPORT === 'sse' ? 'sse' : 'supabase';

export function isSse(): boolean {
  return REALTIME_TRANSPORT === 'sse';
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ===========================================================================
// Shapes the surfaces consume (mirrors the Supabase payloads they read today).
// ===========================================================================

/** Supabase postgres_changes-style payload the Dashboard + NotificationBell read. */
export interface ChangePayload<T = any> {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE' | 'BULK';
  new: T | null;
  old?: T | null;
}

/** Broadcast payload the InboundSmsToastHost reads (ids only — no PHI). */
export interface InboundSmsPayload {
  contactId?: string;
  ownerUserId?: string | null;
}

// ===========================================================================
// SSE multiplexer (only constructed in 'sse' mode). One EventSource per distinct
// channel set; channels are added by (re)opening with the union of active
// channels. A per-channel listener registry dispatches server frames.
// ===========================================================================

type Frame = { channel: string; event: string; payload: any };
type FrameListener = (frame: Frame) => void;

class SseMultiplexer {
  private es: EventSource | null = null;
  private listeners = new Map<string, Set<FrameListener>>();
  private currentSite: string | null = null;
  private backoff = 1000;
  private readonly maxBackoff = 30_000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private opening = false;

  /** Subscribe a listener to a channel. Returns unsubscribe. */
  subscribe(channel: string, siteId: string | null, listener: FrameListener): () => void {
    if (!this.listeners.has(channel)) this.listeners.set(channel, new Set());
    this.listeners.get(channel)!.add(listener);
    this.currentSite = siteId ?? this.currentSite;
    // (Re)open so the new channel is included in the connection's channel set.
    void this.reopen();

    return () => {
      const set = this.listeners.get(channel);
      if (set) {
        set.delete(listener);
        if (set.size === 0) this.listeners.delete(channel);
      }
      if (this.listeners.size === 0) {
        this.close();
      } else {
        void this.reopen();
      }
    };
  }

  private channelList(): string[] {
    return Array.from(this.listeners.keys());
  }

  private close() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.es) {
      this.es.close();
      this.es = null;
    }
  }

  /** Open (or reopen) the EventSource with the current channel union + a fresh token. */
  private async reopen(): Promise<void> {
    if (this.opening) return;
    this.opening = true;
    try {
      const channels = this.channelList();
      if (channels.length === 0) {
        this.close();
        return;
      }
      // Fresh token each (re)connect so a rotated session reconnects cleanly.
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) {
        // No session — retry with backoff (the user may still be signing in).
        this.scheduleReconnect();
        return;
      }
      this.close();

      const params = new URLSearchParams();
      params.set('token', token);
      // The CRM subscribers (crm:inbound-sms, crm:contact:*) subscribe with a
      // null siteId, so `currentSite` is only set when the Dashboard's
      // appointments subscriber is also mounted. On CRM-only pages that leaves
      // `site` unset and the backend's CRM channel auth fail-closes (403). Fall
      // back to the persisted site (same `currentSiteId` the REST X-Site-Id
      // header uses) so CRM realtime works on every page.
      const site = this.currentSite ?? getCurrentSiteId();
      if (site) params.set('site', site);
      params.set('channels', channels.join(','));

      const es = new EventSource(`${API_URL}/api/events?${params.toString()}`);
      this.es = es;

      es.addEventListener('open', () => {
        this.backoff = 1000; // reset backoff on a good connection
      });

      es.addEventListener('message', (ev: MessageEvent) => {
        this.dispatch(ev.data);
      });

      // The server also emits a named "ready" event listing granted channels; we
      // don't need to act on it, but listening avoids it surfacing as an error.
      es.addEventListener('ready', () => {});

      es.addEventListener('error', () => {
        // EventSource auto-reconnects on network blips, but on an auth failure the
        // stream just errors repeatedly. Close and reconnect ourselves with a
        // fresh token after backoff (covers expired-JWT + transient drops).
        this.close();
        this.scheduleReconnect();
      });
    } finally {
      this.opening = false;
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return;
    const delay = this.backoff;
    this.backoff = Math.min(this.backoff * 2, this.maxBackoff);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.reopen();
    }, delay);
  }

  private dispatch(raw: string) {
    let frame: Frame;
    try {
      frame = JSON.parse(raw);
    } catch {
      return;
    }
    const set = this.listeners.get(frame.channel);
    if (!set) return;
    for (const l of set) {
      try {
        l(frame);
      } catch {
        /* a listener throwing must not kill the stream */
      }
    }
  }
}

// Lazily constructed so 'supabase' builds never instantiate an EventSource.
let mux: SseMultiplexer | null = null;
function multiplexer(): SseMultiplexer {
  if (!mux) mux = new SseMultiplexer();
  return mux;
}

// ===========================================================================
// Per-surface subscribe helpers. Each preserves the exact handler contract the
// corresponding consumer had against Supabase.
// ===========================================================================

/**
 * Dashboard — appointment changes for a site. `onChange` receives the same
 * postgres_changes-style payload it read before ({ eventType, new, old }).
 */
export function subscribeAppointments(
  siteId: string,
  onChange: (payload: ChangePayload) => void,
): () => void {
  if (isSse()) {
    const unsub = multiplexer().subscribe(`appointments:${siteId}`, siteId, (frame) => {
      onChange(frame.payload as ChangePayload);
    });
    return unsub;
  }
  // Supabase path — verbatim the Dashboard's original subscription.
  const channel: RealtimeChannel = supabase
    .channel(`appointments-${siteId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'appointments', filter: `site_id=eq.${siteId}` },
      (payload: any) => onChange(payload as ChangePayload),
    )
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * IXVerify Notifications PAGE — refetch on ANY notification change for the user.
 * `onChange` takes no argument (the page just refetches), matching the original.
 */
export function subscribeIxverifyNotificationsAll(
  userId: string,
  onChange: () => void,
): () => void {
  if (isSse()) {
    return multiplexer().subscribe(`ixverify:${userId}`, null, () => onChange());
  }
  const channel = supabase
    .channel('notifications-page')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'ixverify_notifications', filter: `user_id=eq.${userId}` },
      () => onChange(),
    )
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * IXVerify NotificationBell — INSERT and UPDATE for the user, each delivering the
 * row (`payload.new`). Matches the bell's two-handler original.
 */
export function subscribeIxverifyNotificationsBell<T = any>(
  userId: string,
  onInsert: (row: T) => void,
  onUpdate: (row: T) => void,
): () => void {
  if (isSse()) {
    return multiplexer().subscribe(`ixverify:${userId}`, null, (frame) => {
      const p = frame.payload as ChangePayload<T>;
      if (p.eventType === 'INSERT' && p.new) onInsert(p.new);
      else if (p.eventType === 'UPDATE' && p.new) onUpdate(p.new);
    });
  }
  const channel = supabase
    .channel('ixverify_notifications')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'ixverify_notifications', filter: `user_id=eq.${userId}` },
      (payload: any) => onInsert(payload.new as T),
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'ixverify_notifications', filter: `user_id=eq.${userId}` },
      (payload: any) => onUpdate(payload.new as T),
    )
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * CRM CommsThread — content-free "changed" ping on a contact's topic. `onPing`
 * takes no argument (the thread re-fetches through the authed API), matching the
 * original broadcast handler.
 */
export function subscribeContactChanges(contactId: string, onPing: () => void): () => void {
  if (isSse()) {
    return multiplexer().subscribe(`crm:contact:${contactId}`, null, () => onPing());
  }
  const channel = supabase
    .channel(`crm:contact:${contactId}`)
    .on('broadcast', { event: 'changed' }, () => onPing())
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * CRM InboundSmsToastHost — the fixed network-wide inbound-SMS topic. `onSms`
 * receives the ids-only payload ({ contactId, ownerUserId }), matching the
 * original broadcast handler's `msg.payload`.
 */
export function subscribeInboundSms(onSms: (payload: InboundSmsPayload) => void): () => void {
  if (isSse()) {
    return multiplexer().subscribe('crm:inbound-sms', null, (frame) => {
      onSms((frame.payload || {}) as InboundSmsPayload);
    });
  }
  const channel = supabase
    .channel('crm:inbound-sms')
    .on('broadcast', { event: 'sms_inbound' }, (msg: any) => {
      onSms((msg?.payload || {}) as InboundSmsPayload);
    })
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}

export default {
  REALTIME_TRANSPORT,
  isSse,
  subscribeAppointments,
  subscribeIxverifyNotificationsAll,
  subscribeIxverifyNotificationsBell,
  subscribeContactChanges,
  subscribeInboundSms,
};
